import React, { ChangeEvent, KeyboardEvent, useState } from 'react';
import { FieldErrors, UseFormRegisterReturn } from 'react-hook-form';

import { TextInputThemeClasses } from '../../styles/text-input/theme-classes';
import { tw } from '../../utils';

export interface TextInputProps {
  name: string;
  theme?: 'light' | 'dark';
  type?: 'text' | 'textarea' | 'email'; // Make type optional and default to 'text'
  register?: UseFormRegisterReturn<any>;
  placeholder?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyUp?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  borderRadius?: 'default' | 'large';
  errorMessages?: FieldErrors<any>;
  disabled?: boolean;
  hideLabel?: boolean;
  labelText?: string;
  rows?: number; // New prop for textarea rows
  minLength?: number; // New prop for min number of characters
  maxLength?: number; // New prop for max number of characters
  showCharCount?: boolean; // New prop to show character count
}

export const TextInput: React.FC<TextInputProps> = ({
  name,
  theme = 'dark',
  type = 'text', // Default type to 'text'
  placeholder,
  onChange,
  onKeyUp,
  register,
  borderRadius = 'default',
  errorMessages,
  disabled,
  hideLabel = false,
  labelText,
  rows = 3,
  minLength,
  maxLength,
  showCharCount = false,
  ...props
}: TextInputProps) => {
  const [charCount, setCharCount] = useState(0);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (onChange) onChange(e);
    setCharCount(e.target.value.length);
  };

  return (
    <div className='flex flex-col space-y-2'>
      {!hideLabel ? (
        <label
          htmlFor={name}
          className='font-kumbh text-sm uppercase tracking-wider text-white/70'
        >
          <div className='mb-2 flex w-full justify-between'>
            <div> {labelText ?? name}</div>
            {showCharCount ? (
              <div>
                {charCount}/{maxLength}
              </div>
            ) : null}
          </div>
        </label>
      ) : null}
      {type === 'textarea' ? (
        <textarea
          id={name}
          {...(register && register)}
          onChange={handleChange}
          onKeyUp={onKeyUp}
          disabled={disabled}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          {...props}
          className={tw(
            `${TextInputThemeClasses[theme]}`,
            'font-kumbh rounded-lg border-none font-thin placeholder:text-sm',
            'placeholder:font-kumbh placeholder:font-thin placeholder:tracking-wider',
            'resize-none focus:outline-none focus:ring-1 disabled:cursor-not-allowed',
            borderRadius === 'large'
              ? 'rounded-xl px-8 py-2'
              : 'rounded px-5 py-3',
            errorMessages?.[name] && 'ring-dullsville-red-pale ring-1',
          )}
        />
      ) : (
        <input
          type={type}
          id={name}
          {...(register && register)}
          onChange={handleChange}
          onKeyUp={onKeyUp}
          disabled={disabled}
          placeholder={placeholder}
          minLength={minLength}
          maxLength={maxLength}
          {...props}
          className={tw(
            `${TextInputThemeClasses[theme]}`,
            'font-kumbh rounded-full border-none font-thin placeholder:text-sm',
            'placeholder:font-kumbh placeholder:font-thin placeholder:tracking-wider',
            'focus:outline-none disabled:cursor-not-allowed',
            borderRadius === 'large'
              ? 'rounded-2xl px-10 py-4'
              : 'rounded px-5 py-3',
            errorMessages?.[name] && 'ring-dullsville-red-pale ring-1',
          )}
        />
      )}
      {errorMessages?.[name] ? (
        <p className='bg-dullsville-red-pale font-kumbh rounded-lg px-3 py-2 text-xs text-white'>
          {errorMessages[name]?.message === ''
            ? 'Required Field'
            : errorMessages[name]?.message?.toString()}
        </p>
      ) : null}
    </div>
  );
};
